import { useSyncExternalStore } from "react";
import { api, mediaUrl } from "@/lib/api";

// ---- Script / caption font loading (Telugu, Devanagari, Latin families) ----
// Canvas 2D silently falls back to a glyphless font when a web font hasn't been
// downloaded yet. Google Fonts load lazily and are never applied to DOM text, so
// the Telugu/Devanagari subsets never download → tofu in the canvas preview.
// We force-load them via the FontFace API (document.fonts.load) and notify
// listeners so every canvas redraws once the real glyphs are ready.
const SCRIPT_SAMPLES = {
  "Noto Sans Telugu": "మన ఇండియా టీమ్ చాలా బాగా ఆడింది",
  "Noto Sans Devanagari": "नमस्ते भारत टीम",
  "Noto Sans Tamil": "தமிழ் வணக்கம் இந்தியா",
  "Noto Sans Malayalam": "മലയാളം നമസ്കാരം",
  "Noto Sans Kannada": "ಕನ್ನಡ ನಮಸ್ಕಾರ",
};
const CORE_FAMILIES = ["Montserrat", "Poppins", "Oswald", "Anton", "Bebas Neue", "Roboto", "Manrope", "Chivo", "IBM Plex Sans", "Inter"];

const _fontReadyListeners = new Set();
export function onFontsReady(cb) { _fontReadyListeners.add(cb); return () => _fontReadyListeners.delete(cb); }
const _notifyFonts = () => _fontReadyListeners.forEach((l) => { try { l(); } catch { /* noop */ } });

let _scriptFontsPromise = null;
// Force-load the built-in script fonts + core Latin families once. Idempotent.
export function ensureScriptFonts() {
  if (_scriptFontsPromise) return _scriptFontsPromise;
  if (typeof document === "undefined" || !document.fonts) return Promise.resolve();
  const jobs = [];
  for (const [fam, sample] of Object.entries(SCRIPT_SAMPLES)) {
    for (const w of [400, 700]) jobs.push(document.fonts.load(`${w} 90px "${fam}"`, sample).catch(() => {}));
  }
  for (const fam of CORE_FAMILIES) jobs.push(document.fonts.load(`700 90px "${fam}"`).catch(() => {}));
  _scriptFontsPromise = Promise.all(jobs).then(() => _notifyFonts());
  return _scriptFontsPromise;
}

// Ensure the fonts required for a specific piece of text are loaded (script-aware),
// then notify canvases to redraw. Cheap on repeat (browser caches loaded fonts).
export async function ensureTextFonts(text, families = []) {
  if (typeof document === "undefined" || !document.fonts || !text) return;
  const jobs = [];
  const sample = text.slice(0, 400);
  if (/[\u0C00-\u0C7F]/.test(text)) {
    for (const w of [400, 700]) jobs.push(document.fonts.load(`${w} 90px "Noto Sans Telugu"`, sample).catch(() => {}));
    const teFams = new Set(families.filter(Boolean).map(teluguFamilyFor));
    teFams.forEach((fam) => [400, 700].forEach((w) => jobs.push(document.fonts.load(`${w} 90px "${fam}"`, sample).catch(() => {}))));
  }
  if (/[\u0900-\u097F]/.test(text)) for (const w of [400, 700]) jobs.push(document.fonts.load(`${w} 90px "Noto Sans Devanagari"`, sample).catch(() => {}));
  if (/[\u0B80-\u0BFF]/.test(text)) for (const w of [400, 700]) jobs.push(document.fonts.load(`${w} 90px "Noto Sans Tamil"`, sample).catch(() => {}));
  if (/[\u0D00-\u0D7F]/.test(text)) for (const w of [400, 700]) jobs.push(document.fonts.load(`${w} 90px "Noto Sans Malayalam"`, sample).catch(() => {}));
  if (/[\u0C80-\u0CFF]/.test(text)) for (const w of [400, 700]) jobs.push(document.fonts.load(`${w} 90px "Noto Sans Kannada"`, sample).catch(() => {}));
  for (const fam of families) if (fam) jobs.push(document.fonts.load(`700 90px "${fam}"`, sample).catch(() => {}));
  if (jobs.length) { await Promise.all(jobs); _notifyFonts(); }
}

// Collect all rendered text from caption layers + transcript so we can preload
// exactly the glyph subsets the preview will draw.
export function collectCaptionText(layers, segments) {
  let out = "";
  (layers || []).forEach((l) => (l.objects || []).forEach((o) => { out += " " + (o.text || ""); }));
  (segments || []).forEach((s) => { out += " " + (s.text || ""); (s.words || []).forEach((w) => { out += " " + (w.word || ""); }); });
  return out.trim();
}

// Reactive store for user-uploaded custom fonts. Fonts are registered as
// FontFace objects so the canvas preview can render them, and their families
// are surfaced to every font picker. Export resolves them server-side.
let _fonts = [];
const _registered = new Set();
const _listeners = new Set();

const emit = () => { _listeners.forEach((l) => l()); };
const subscribe = (l) => { _listeners.add(l); return () => _listeners.delete(l); };
const getSnapshot = () => _fonts;

export function useCustomFonts() {
  return useSyncExternalStore(subscribe, getSnapshot);
}

export async function registerFontFace(family, storagePath) {
  if (!family || !storagePath || _registered.has(family)) return;
  _registered.add(family);
  try {
    const ff = new FontFace(family, `url("${mediaUrl(storagePath)}")`);
    await ff.load();
    document.fonts.add(ff);
  } catch {
    _registered.delete(family);
  }
}

export async function loadCustomFonts() {
  try {
    const list = await api.fonts.list();
    _fonts = list;
    emit();
    await Promise.all(list.map((f) => registerFontFace(f.family, f.storage_path)));
  } catch {
    /* not logged in / none */
  }
}

export async function uploadFont(file) {
  const doc = await api.fonts.upload(file);
  await registerFontFace(doc.family, doc.storage_path);
  _fonts = [doc, ..._fonts.filter((f) => f.id !== doc.id)];
  emit();
  return doc; // includes glyph_warning
}

export async function deleteFont(id) {
  await api.fonts.delete(id);
  _fonts = _fonts.filter((f) => f.id !== id);
  emit();
}

// ---- Telugu: Telugu-ready families + auto-match for Latin-only fonts (mirrors backend animation_engine.telugu_match) ----
export const TELUGU_FONTS = ["Noto Sans Telugu", "Ramabhadra", "Mallanna", "Noto Serif Telugu", "Baloo Tammudu 2", "Mandali", "NTR",
  "Gurajada", "Suravaram", "Anek Telugu", "Tenali Ramakrishna", "Timmana", "Dhurjati", "Ramaraja", "Sree Krushnadevaraya",
  "Hind Guntur", "Suranna", "Peddana", "Ponnala", "Gidugu"];
const TE_MATCH = [
  [["great vibes", "dancing script", "pacifico", "yellowtail", "kaushan", "script", "hand", "brush", "cursive"], "Mallanna"],
  [["playfair", "lora", "merriweather", "georgia", "times", "serif"], "Noto Serif Telugu"],
  [["anton", "bebas", "oswald", "teko", "archivo black", "righteous", "display", "impact", "condensed"], "Ramabhadra"],
  [["nunito", "poppins", "rubik", "baloo", "rounded"], "Baloo Tammudu 2"],
];
export function teluguMatch(family) {
  const f = String(family || "").toLowerCase();
  if (!f.includes("sans") || f.replace("sans", "").includes("serif")) {
    for (const [keys, te] of TE_MATCH) if (keys.some((k) => f.includes(k))) return te;
  }
  return "Noto Sans Telugu";
}
export function isTeluguReady(family) {
  if (!family) return false;
  if (TELUGU_FONTS.includes(family)) return true;
  const hit = (_libraryFonts || []).find((f) => f.family === family) || (_fonts || []).find((f) => f.family === family);
  return !!hit?.has_telugu;
}
// Family to draw Telugu text with: the chosen font when it has Telugu letters, else a matching Telugu font.
export const teluguFamilyFor = (family) => (isTeluguReady(family) ? family : teluguMatch(family));

// ---- Global admin-managed font library (offered in addition to built-ins + custom) ----
// Curated built-in Google families that are always available in the picker.
export const GOOGLE_FONTS = [
  "Montserrat", "Poppins", "Inter", "Roboto", "Manrope", "Oswald", "Anton", "Bebas Neue",
  "Chivo", "IBM Plex Sans", "Playfair Display", "Lora", "Merriweather", "Raleway", "Nunito",
  "Work Sans", "Rubik", "Archivo", "Barlow", "DM Sans", "Space Grotesk", "Teko", "Righteous",
  "Great Vibes", "Dancing Script", "Pacifico", "Yellowtail", "Kaushan Script", "Archivo Black",
  ...TELUGU_FONTS,
];

let _libraryFonts = [];
const _libListeners = new Set();
const _emitLib = () => { _libListeners.forEach((l) => l()); };
const _subLib = (l) => { _libListeners.add(l); return () => _libListeners.delete(l); };
const _snapLib = () => _libraryFonts;

export function useLibraryFonts() {
  return useSyncExternalStore(_subLib, _snapLib);
}

// Fetch the enabled global fonts, register their FontFaces so previews render the real font.
export async function loadLibraryFonts() {
  try {
    const list = await api.fontsLibrary();
    _libraryFonts = list || [];
    _emitLib();
    await Promise.all((_libraryFonts || []).map((f) => registerFontFace(f.family, f.storage_path)));
    _notifyFonts();
  } catch {
    /* not logged in / none — non-fatal */
  }
}

// Combined, de-duplicated font options for any shared picker.
// [{ family, name, source: 'library'|'custom'|'builtin', category, tags, featured }]
export function buildFontOptions() {
  const seen = new Set();
  const out = [];
  (_libraryFonts || []).forEach((f) => {
    if (seen.has(f.family)) return;
    seen.add(f.family);
    out.push({ family: f.family, name: f.name || f.family, source: "library",
      category: f.category, tags: [...(f.tags || []), ...(f.has_telugu ? ["telugu"] : [])], featured: !!f.featured, telugu: !!f.has_telugu });
  });
  (_fonts || []).forEach((f) => {
    if (seen.has(f.family)) return;
    seen.add(f.family);
    out.push({ family: f.family, name: f.family, source: "custom", category: "My Uploads", tags: f.has_telugu ? ["telugu"] : [], telugu: !!f.has_telugu });
  });
  GOOGLE_FONTS.forEach((fam) => {
    if (seen.has(fam)) return;
    seen.add(fam);
    const te = TELUGU_FONTS.includes(fam);
    out.push({ family: fam, name: fam, source: "builtin", category: te ? "Telugu" : "Built-in", tags: te ? ["telugu"] : [], telugu: te });
  });
  return out;
}
